import React, { useEffect, useState } from "react";
import { X, ExternalLink } from "lucide-react";

/**
 * Slim announcement ribbon shown to visitors on the platform-issued URL
 * (soul-food-table.emergent.host) or the preview URL, nudging them to
 * bookmark the branded custom domain. Hidden on /kitchen wall-TV,
 * hidden once user dismisses, and hidden entirely if there is no
 * canonical host configured yet.
 */
export default function CustomDomainRibbon() {
  const canonical = (process.env.REACT_APP_CANONICAL_HOST || "").trim().toLowerCase();
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    if (!canonical) return;
    if (typeof window === "undefined") return;
    const here = window.location.hostname.toLowerCase();
    // If already on the canonical domain, nothing to announce.
    if (here === canonical) return;
    // Only show on the emergent-issued production URL — preview is owner-only,
    // so we don't nag ourselves during development.
    if (!here.endsWith(".emergent.host")) return;
    // Don't nag on the kitchen wall-TV.
    if (window.location.pathname === "/kitchen") return;
    // Respect prior dismissal (7-day cool-off).
    const raw = localStorage.getItem("mm_ribbon_dismissed");
    if (raw) {
      const ts = parseInt(raw, 10);
      if (!Number.isNaN(ts) && Date.now() - ts < 1000 * 60 * 60 * 24 * 7) return;
    }
    setDismissed(false);
  }, [canonical]);

  if (dismissed || !canonical) return null;

  const dismiss = () => {
    localStorage.setItem("mm_ribbon_dismissed", String(Date.now()));
    setDismissed(true);
  };

  const branded = `https://${canonical}${window.location.pathname}${window.location.search}`;

  return (
    <div
      data-testid="custom-domain-ribbon"
      className="w-full bg-[var(--mm-accent)] text-[#1a1512] text-[13px] tracking-wide"
      style={{ position: "sticky", top: 0, zIndex: 60 }}
    >
      <div className="max-w-6xl mx-auto flex items-center justify-between gap-3 px-4 py-2">
        <div className="flex-1 min-w-0 truncate">
          <span className="hidden sm:inline uppercase font-semibold mr-2">New home:</span>
          <a
            href={branded}
            className="underline decoration-dotted underline-offset-2 font-medium hover:decoration-solid inline-flex items-center gap-1"
          >
            {canonical}
            <ExternalLink size={11} />
          </a>
          <span className="ml-2 opacity-80 hidden md:inline">
            — bookmark our new home. This address still works for now.
          </span>
        </div>
        <button
          onClick={dismiss}
          data-testid="ribbon-dismiss"
          aria-label="Dismiss"
          className="p-1 rounded hover:bg-black/10 transition"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
